import { type FormEvent, useState } from "react";

import { session } from "../auth";
import logo from "../logo.svg";
import { Button } from "../ui";
import { Spinner } from "../ui/Spinner";

/**
 * The whole app behind one shared password.
 *
 * A plain form, submitted rather than button-clicked, so a password manager
 * recognises it and Enter works on a phone keyboard. The username field is
 * hidden but present for the same reason — managers save nothing without one.
 */
export function Login() {
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	const submit = async (e: FormEvent) => {
		e.preventDefault();
		if (!password || busy) return;
		setBusy(true);
		setError(null);
		const message = await session.login(password);
		if (message) {
			setError(message);
			setPassword("");
			setBusy(false);
			return;
		}
		// On success the session store flips the app over to the shell; leaving
		// `busy` set avoids a flash of the enabled form during that swap.
	};

	return (
		<div className="flex min-h-screen items-center justify-center bg-ground px-4 text-ink">
			<div className="w-full max-w-xs">
				<img src={logo} alt="" className="mx-auto mb-3 size-10" />
				<h1 className="mb-1 text-center text-2xl font-bold font-tag">scalpmania</h1>
				<p className="mb-5 text-center text-[13px] text-muted">Enter the password to continue.</p>

				<form onSubmit={submit} className="rounded-[14px] border border-hairline bg-paper p-4">
					<input type="text" name="username" autoComplete="username" value="scalpmania" readOnly hidden />
					<label className="mb-1.5 block text-xs text-muted" htmlFor="password">
						Password
					</label>
					<input
						id="password"
						type="password"
						autoComplete="current-password"
						autoFocus
						value={password}
						onChange={(e) => {
							setPassword(e.target.value);
							setError(null);
						}}
						aria-invalid={error ? true : undefined}
						aria-describedby={error ? "login-error" : undefined}
						className="mb-3 h-9 w-full rounded-md border border-hairline bg-ground px-3 text-sm text-ink outline-none placeholder:text-muted focus:border-line"
					/>
					<Button type="submit" variant="primary" disabled={!password || busy} className="w-full">
						{busy ? <Spinner className="text-ground" /> : "Sign in"}
					</Button>
					{error && (
						<p id="login-error" role="alert" className="mt-3 text-center text-xs text-fault">
							{error}
						</p>
					)}
				</form>
			</div>
		</div>
	);
}
